import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Button, 
  Card, 
  CardContent, 
  useTheme, 
  Paper,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  Fade
} from '@mui/material';
import { 
  FaWhatsapp, 
  FaMobile, 
  FaSchool, 
  FaCalendarAlt, 
  FaHandshake, 
  FaRobot, 
  FaHeart, 
  FaUsers, 
  FaUserFriends,
  FaChevronRight
} from 'react-icons/fa';
import { APP_ROUTES } from '../../utils/constants';

/**
 * Solutions page component with corporate design
 */
const Solutions = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  
  // Handle navigation
  const handleNavigation = (path) => {
    navigate(path);
  };
  
  // Core solutions data
  const coreSolutions = [
    {
      id: 1,
      title: 'Parent AI Assistant',
      subtitle: 'Parenting, Simplified.',
      icon: <FaWhatsapp />,
      features: [
        'Our AI-powered WhatsApp assistant gives parents instant access to expert guidance on school selection, curriculum understanding (CBC, IGCSE, 8-4-4, and more), and holistic parenting advice.',
        'Designed to meet you where you are — on the devices you already use, in the language you understand.'
      ],
      ctaText: 'Start a Chat on WhatsApp',
      ctaLink: APP_ROUTES.CONTACT
    },
    {
      id: 2,
      title: 'USSD Education Platform',
      subtitle: 'Education Support for Every Device.',
      icon: <FaMobile />,
      features: [
        'Recognising device and data limitations, our USSD platform brings key educational support — from school information to parenting tips — straight to any mobile phone, without the need for smartphones or internet.',
        'A commitment to leave no parent or learner behind.'
      ],
      ctaText: 'See How It Works',
      ctaLink: APP_ROUTES.ABOUT
    },
    {
      id: 3,
      title: 'Teacher AI Companion',
      subtitle: 'Empowering Teachers, One Lesson at a Time.',
      icon: <FaSchool />,
      features: [
        'Our AI companion provides teachers with practical lesson planning support, classroom management strategies, and continuous professional growth resources — aligned to African curricula and realities.',
        'Built in partnership with educators, to truly serve educators.'
      ],
      ctaText: 'Coming Soon',
      ctaLink: '/parent/schools',
      disabled: true
    },
    {
      id: 4,
      title: 'School Communication Hub',
      subtitle: 'Stronger School-Parent Partnerships.',
      icon: <FaCalendarAlt />,
      features: [
        'Our smart communication platform helps schools keep parents informed through seamless updates, event announcements, curriculum updates, newsletters, and fee reminders — powered by AI on familiar platforms like WhatsApp and SMS.',
        'Bringing schools and families closer, effortlessly.'
      ],
      ctaText: 'View Upcoming Events',
      ctaLink: '/events'
    },
    {
      id: 5,
      title: 'Meira Events',
      subtitle: 'Community-First Learning Experiences.',
      icon: <FaHandshake />,
      features: [
        'We host regular events, webinars, and workshops designed to empower parents, teachers, and school leaders with the latest insights on education, technology, and child development.',
        'Where learning meets community, and knowledge sparks action.'
      ],
      ctaText: 'Partner With Us',
      ctaLink: APP_ROUTES.CONTACT
    }
  ];
  
  // Trust factors data
  const trustFactors = [
    {
      id: 1,
      title: 'Tech With Heart',
      description: 'AI and USSD designed to support, not overwhelm.',
      icon: <FaRobot />
    },
    {
      id: 2,
      title: 'Equity-Driven',
      description: 'Every parent, whether in Nairobi or Nyandarua, deserves access to guidance.',
      icon: <FaHeart />
    },
    {
      id: 3,
      title: 'Community + Expertise',
      description: 'We blend real-life wisdom from parents with the best research and professional insights.',
      icon: <FaUserFriends />
    }
  ];
  
  // Ecosystem elements
  const ecosystemElements = [
    { id: 1, name: 'Meira AI', icon: <FaRobot /> },
    { id: 2, name: 'Schools Connect', icon: <FaSchool /> },
    { id: 3, name: 'Professionals', icon: <FaUserFriends /> },
    { id: 4, name: 'Events', icon: <FaCalendarAlt /> },
    { id: 5, name: 'USSD Access', icon: <FaMobile /> },
    { id: 6, name: 'Meira Events', icon: <FaHandshake /> }
  ];

  return (
    <>
      <Helmet>
        <title>Our Solutions | Meira Education</title>
        <meta name="description" content="Explore Meira's ecosystem of support for parents, schools, and children. From AI tools to school directories, we empower parents across Kenya." />
      </Helmet>

      {/* Hero Section */}
      <Box 
        sx={{ 
          bgcolor: theme.palette.primary.main,
          color: 'white',
          py: { xs: 10, md: 16 },
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {/* Background decoration */}
        <Box 
          sx={{ 
            position: 'absolute',
            right: -100,
            bottom: -80,
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
            zIndex: 1
          }}
        />
        
        <Container maxWidth="lg">
          <Fade in={true} timeout={1000}>
            <Grid container spacing={4} justifyContent="center">
              <Grid item xs={12} md={10} textAlign="center">
                <Typography 
                  variant="h2" 
                  component="h1" 
                  fontWeight="bold"
                  gutterBottom
                  sx={{ 
                    fontSize: { xs: '2.5rem', md: '3.5rem' }
                  }}
                >
                  An Ecosystem of Support—Designed for You.
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    opacity: 0.9, 
                    mb: 4, 
                    maxWidth: 800, 
                    mx: 'auto' 
                  }}
                >
                  We don't believe in one-size-fits-all parenting advice. That's why we created a multi-tiered, multi-stakeholder approach to support every parent, no matter their background, location, or stage in the journey.
                </Typography>
                
                <Box 
                  sx={{ 
                    display: 'flex', 
                    gap: 3, 
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    mt: 6
                  }}
                >
                  
                </Box>
              </Grid>
            </Grid>
          </Fade>
        </Container>
      </Box>

      {/* Our Ecosystem at a Glance */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'grey.50' }}>
        <Container maxWidth="lg">
          
          
          {/* Ecosystem Visualization */}
          <Paper 
            elevation={2}
            sx={{ 
              maxWidth: 900,
              mx: 'auto',
              borderRadius: 4,
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <Box 
              sx={{ 
                p: { xs: 4, md: 6 },
                background: 'linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(248,250,255,1) 100%)'
              }}
            >
              <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: theme.palette.primary.main,
                    width: 90,
                    height: 90,
                    mx: 'auto',
                    mb: 3,
                    boxShadow: 3
                  }}
                >
                  <FaUsers style={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
                  Parental Education Tools
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 450, mx: 'auto' }}>
                  At the center of everything we do are parents and families, supported by our integrated ecosystem
                </Typography>
              </Box>
              
              <Grid 
                container 
                spacing={3} 
                sx={{ 
                  maxWidth: 760,
                  mx: 'auto'
                }}
              >
                {ecosystemElements.map(element => (
                  <Grid item xs={6} sm={4} key={element.id}>
                    <Box 
                      sx={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center'
                      }}
                    >
                      <Avatar 
                        sx={{ 
                          bgcolor: 'rgba(0, 118, 255, 0.1)',
                          color: theme.palette.primary.main,
                          width: 60,
                          height: 60,
                          mb: 1,
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: -5,
                            left: -5,
                            right: -5,
                            bottom: -5,
                            borderRadius: '50%',
                            border: `2px dashed ${theme.palette.primary.light}`,
                            opacity: 0.5
                          }
                        }}
                      >
                        {element.icon}
                      </Avatar>
                      <Typography variant="body1" fontWeight="medium">
                        {element.name}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Our Core Solutions */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="overline" 
              component="div" 
              color="primary" 
              fontWeight="bold"
            >
              Our Core Solutions
            </Typography>
            <Typography 
              variant="h3" 
              component="h2" 
              fontWeight="bold" 
              gutterBottom
            >
              Tools Designed for Every Parent
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                maxWidth: 700, 
                mx: 'auto', 
                fontSize: '1.1rem' 
              }}
            >
              We've created a comprehensive suite of solutions to support every aspect of your parenting journey
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {coreSolutions.map((solution) => (
              <Grid item xs={12} md={6} lg={4} key={solution.id}>
                <Card 
                  elevation={2}
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6
                    },
                    overflow: 'visible',
                    position: 'relative'
                  }}
                >
                  <Box 
                    sx={{ 
                      position: 'absolute',
                      top: -20,
                      left: 20,
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      bgcolor: theme.palette.primary.main,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: 3,
                      zIndex: 1
                    }}
                  >
                    {React.cloneElement(solution.icon, { style: { fontSize: 28 } })}
                  </Box>
                  
                  <CardContent sx={{ p: 4, pt: 5, flexGrow: 1 }}>
                    <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom>
                      {solution.title}
                    </Typography>
                    <Typography 
                      variant="subtitle1" 
                      color="text.secondary" 
                      gutterBottom
                      sx={{ mb: 3 }}
                    >
                      {solution.subtitle}
                    </Typography>
                    
                    <List sx={{ mb: 3 }}>
                      {solution.features.map((feature, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 30, color: theme.palette.primary.main }}>
                            <FaChevronRight size={12} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature}
                            primaryTypographyProps={{ 
                              variant: 'body2',
                              color: 'text.secondary'
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                    
                    <Box sx={{ mt: 'auto' }}>
                      <Button 
                        variant="contained" 
                        color="primary"
                        fullWidth
                        disabled={solution.disabled}
                        onClick={() => handleNavigation(solution.ctaLink)}
                        sx={{ 
                          borderRadius: 2,
                          py: 1,
                          textTransform: 'none',
                          fontWeight: 'bold'
                        }}
                      >
                        {solution.ctaText}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* What Makes Our Approach Unique */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: 'grey.50' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="overline" 
              component="div" 
              color="primary" 
              fontWeight="bold"
            >
              Our Approach
            </Typography>
            <Typography 
              variant="h3" 
              component="h2" 
              fontWeight="bold" 
              gutterBottom
            >
              Why Parents Trust Meira
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                maxWidth: 700, 
                mx: 'auto', 
                fontSize: '1.1rem' 
              }}
            >
              What makes our approach different from other educational solutions
            </Typography>
          </Box>
          
          <Grid container spacing={4} justifyContent="center">
            {trustFactors.map((factor) => (
              <Grid item xs={12} sm={6} md={4} key={factor.id}>
                <Paper
                  elevation={2}
                  sx={{ 
                    p: 4, 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    borderRadius: 3,
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <Avatar 
                    sx={{ 
                      bgcolor: theme.palette.primary.main,
                      width: 70,
                      height: 70,
                      mb: 3
                    }}
                  >
                    {React.cloneElement(factor.icon, { style: { fontSize: 32 } })}
                  </Avatar>
                  
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {factor.title}
                  </Typography>
                  
                  <Typography variant="body1" color="text.secondary">
                    {factor.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box 
        sx={{ 
          bgcolor: theme.palette.primary.main,
          color: 'white',
          py: { xs: 6, md: 8 }
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography variant="h3" component="h2" fontWeight="bold" gutterBottom>
                Ready to get started?
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                Connect with Meira today and transform your educational journey.
              </Typography>
            </Grid>
            
            <Grid 
              item 
              xs={12} 
              md={5}
              sx={{ 
                display: 'flex', 
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: { xs: 'center', md: 'flex-end' }
              }}
            >
              <Button 
                variant="contained" 
                color="secondary" 
                size="large"
                onClick={() => handleNavigation(APP_ROUTES.REGISTER)}
                sx={{ 
                  px: 4,
                  py: 1.5,
                  fontWeight: 'bold',
                  borderRadius: 2
                }}
              >
                Sign Up Free
              </Button>
              <Button 
                variant="outlined" 
                color="inherit" 
                size="large"
                onClick={() => handleNavigation(APP_ROUTES.CONTACT)}
                sx={{ 
                  px: 4,
                  py: 1.5,
                  borderColor: 'white',
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Contact Us
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default Solutions; 